import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { ICellEditorAngularComp, ICellRendererAngularComp } from 'ag-grid-angular';
import { CurrencyPipe } from '@angular/common';
import { Store } from '@ngrx/store';
import { selectCostBasis, selectMultipleToAccountsPositionLotTableData, selectToAccountsInfo } from '../../../create-request/reducers';
import { takeUntil, take } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { positionConstants } from '../../utilities/Constants';
import { RowDataById } from '../../../create-request/models/internal-transfer/InternalTransfersInfo';
import { InternalTransfersActions } from '../../../create-request/actions';

@Component({
  standalone: false,
    selector: 'mm-text-position-input-editor-multiple-to-accounts',
    templateUrl: './table-text-position-input-field-multiple-to-account.component.html',
    styleUrls: ['./table-text-position-input-field-multiple-to-account.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
})
export class TextFieldMultiplePositionComponent implements ICellEditorAngularComp, ICellRendererAngularComp, OnInit, OnDestroy {
    constructor(private store: Store, private currencyPipe: CurrencyPipe, private changeDetection: ChangeDetectorRef) { }

    @ViewChild('inputShares') textInput: any;
    @ViewChild('tooltip') tooltip: any;
    params: any;
    toolTipErrorArray: string[] = []; // array of errors to convert to tooltipError message
    toolTipError: string = ''; // actual error message to display in tool tip
    toolTipErrorCount: number = 0; // running count of total number of errors to be displayed in tooltip error
    currentValue: null | string | undefined = '';
    currentTextInput: string = '';
    showIcon: boolean = false;
    costBasisMethod: string | undefined;
    destroy$: Subject<boolean> = new Subject<boolean>();
    updateEMVs: boolean = false;
    enteredSharesToMove: string = '';
    private lastRenderSyncKey: string | null = null;

    ngOnInit(): void {
        this.store.select(selectCostBasis).pipe(takeUntil(this.destroy$)).subscribe(info => this.costBasisMethod = info);
    }

    ngOnDestroy(): void {
        this.destroy$.next(true);
        this.destroy$.complete();
    }

    agInit(params: any): void {
        this.handleEnteredPositionData(params);
    }

    // Defer the heavier render-mode recalculations so AG Grid can finish mounting the cell before we push follow-up updates.
    handleEnteredPositionData(params: any) {
        this.params = params;
        this.enteredSharesToMove = this.params.value;
        if (this.params.value !== undefined) {
            setTimeout(() => {
                this.updateErrors(this.getTableSharesToMove());
                if (params.mode === 'render') {
                    if (this.shouldRunRenderSync()) {
                        this.handleTableCalculations();
                        this.updateStorePositions();
                    }
                } else {
                    this.lastRenderSyncKey = null;
                }
                this.changeDetection.detectChanges();
            }, 0);
        }
    }

    refresh(params: any): boolean {
        this.handleEnteredPositionData(params);
        return true;
    }

    //This function returns the value currently in the textbox - called by the grid once after editing is complete
    getValue() {
        this.enteredSharesToMove = isNaN(this.textInput?.nativeElement?.value) || Number(this.textInput?.nativeElement?.value) === 0 ? '' : Number(this.textInput?.nativeElement?.value).toString();
        this.params.api.refreshCells({rowNodes:[this.params.node],force: true});
        return this.enteredSharesToMove
    }

    // Skip the expensive render sync when AG Grid rerenders the same visible state more than once.
    private shouldRunRenderSync(): boolean {
        const nextRenderSyncKey = this.getRenderSyncKey();
        if (nextRenderSyncKey === this.lastRenderSyncKey) {
            return false;
        }

        this.lastRenderSyncKey = nextRenderSyncKey;
        return true;
    }

    private getRenderSyncKey(): string {
        const hasErrorColumn = this.getHasErrorColumn();
        const emvColumn = this.getEmvColumn();

        return [
            this.params?.node?.id ?? this.params?.node?.rowIndex ?? '',
            this.params?.column?.getColId?.() ?? '',
            this.enteredSharesToMove ?? '',
            this.getCurrentAvailableShares(),
            this.params?.data?.[hasErrorColumn] ?? '',
            this.params?.data?.[emvColumn] ?? ''
        ].join('|');
    }

    // Position-level validation updates both the inline error state and the row height so the grid remains stable while typing.
    updateErrors(tableSharesToMove: any) {
        this.showIcon = false;
        const availableShares = this.getAvailableShares();
        this.toolTipErrorArray = [];
        this.toolTipErrorCount = 0;
        let numberofErrors = 0;
        let multipleLineErrorCount = 0;
        if (this.params.value !== '') {
            if (this.hasExceedsAvailableSharesError()) {
                this.addTooltipError(positionConstants.positionQuantityExceedsAvailableShares);
                numberofErrors++;
                this.params.node.setDataValue(this.getHasErrorColumn(), numberofErrors);
                this.params.node.setDataValue(this.getMultipleLineErrorCountColumn(), multipleLineErrorCount);
            }
            if (this.hasDecimalLengthError(availableShares, tableSharesToMove)) {
                this.addTooltipError(positionConstants.positionFractionalQuantityMismatch);
                numberofErrors++;
                multipleLineErrorCount++;
                this.params.node.setDataValue(this.getHasErrorColumn(), numberofErrors);
                this.params.node.setDataValue(this.getMultipleLineErrorCountColumn(), multipleLineErrorCount);
            }
            if (this.hasWholeNumberError(availableShares, tableSharesToMove)) {
                if (this.costBasisMethod === 'None') {
                    this.addTooltipError(positionConstants.wholeNumberManualError);
                }
                else {
                    this.addTooltipError(positionConstants.positionWholeNumberError);
                }
                numberofErrors++;
                this.params.node.setDataValue(this.getHasErrorColumn(), numberofErrors);
                this.params.node.setDataValue(this.getMultipleLineErrorCountColumn(), multipleLineErrorCount);
            }
            if (this.hasDenominationError(tableSharesToMove, availableShares)) {
                const quantity = this.positionLevelData?.Quantity;
                const errorMessage = `Enter a multiple of ${quantity.selectedQuantityMinimumDenomination} (minimum of ${quantity.selectedQuantityMinimum})`;
                this.addTooltipError(errorMessage);
                numberofErrors++;
                if (quantity.selectedQuantityMinimumDenomination.toString().length > 2) {
                    multipleLineErrorCount++;
                }
                this.params.node.setDataValue(this.getHasErrorColumn(), numberofErrors);
                this.params.node.setDataValue(this.getMultipleLineErrorCountColumn(), multipleLineErrorCount);
            }
        }
        if (numberofErrors <= 0) {
            this.params.node.setDataValue(this.getHasErrorColumn(), 0);
            this.params.node.setDataValue(this.getMultipleLineErrorCountColumn(), 0);
        }

        this.store.select(selectToAccountsInfo).pipe(take(1)).subscribe(toAccountInfo => {
            let errorCount = 0;
            let numberOfMultipleLineErrors = 0
            for (const key in toAccountInfo) {
                if (this.params.node.data['hasError ' + key] > 0) {
                    if (this.params.node.data['hasError ' + key] > errorCount) {
                        errorCount = this.params.node.data['hasError ' + key];
                    }
                }
                if (this.params.node.data['multipleLineErrorCount ' + key] > 0) {
                    if (this.params.node.data['multipleLineErrorCount ' + key] > numberOfMultipleLineErrors) {
                        numberOfMultipleLineErrors = this.params.node.data['multipleLineErrorCount ' + key]
                    }
                }
            }
            if (this.params.value !== '') {
                this.setRowHeight(55);
            }
            if (errorCount <= 0) {
                this.showIcon = false;
                this.setRowHeight(positionConstants.tableRowHeight);
            }
        });
        if (this.costBasisMethod === 'None' && numberofErrors) {
            setTimeout(() => {
                this.updateEmv('');
                this.updateTotalCostBasis('');
            }, 10);
        }
        this.params.node.setDataValue(this.getNodeChangeStatusColumn(), false);
    }
    
    hasExceedsAvailableSharesError(): boolean {
        return this.getCurrentAvailableShares() < 0;
    }

    hasDenominationError(sharesToMove: number, totalAvailableShares: number): boolean {
        const quantity = this.positionLevelData?.Quantity;
        if(
            sharesToMove !== 0 
            && sharesToMove !== totalAvailableShares
            && (sharesToMove < quantity?.selectedQuantityMinimum
                || (((sharesToMove) % (parseFloat(quantity?.selectedQuantityMinimumDenomination))) > .001))) {
                    return true;
                }
                return false;
    }

    hasWholeNumberError(availableShares: number, sharesToMove: number): boolean {
        const availableSharesIsDecimal = this.isDecimal(availableShares);
        const sharesToMoveIsDecimal = this.isDecimal(sharesToMove);
        return !availableSharesIsDecimal && sharesToMoveIsDecimal;
    }

    hasDecimalLengthError(availableShares: number, sharesToMove: number): boolean {
        const availableSharesIsDecimal = this.isDecimal(availableShares);
        const sharesToMoveIsDecimal = this.isDecimal(sharesToMove);
        const availableSharesDecimalPlaces = this.getNumberOfDecimalPlaces(availableShares);
        const sharesToMoveDecimalPlaces = this.getNumberOfDecimalPlaces(sharesToMove);
        if (availableSharesIsDecimal) {
            return sharesToMoveIsDecimal && (sharesToMoveDecimalPlaces > availableSharesDecimalPlaces);
        }
        return false;
    }

    getNumberOfDecimalPlaces(value: number): number {
        return value.toString().split('.')[1]?.length ?? 0;
    }

    isDecimal(shares: number): boolean {
        return shares - Math.floor(shares) !== 0;
    }

    afterGuiAttached() {
        this.textInput.nativeElement.focus();
        this.textInput.nativeElement.select();
    }

    updateSharesToMove(lotsToMove: number | string) {
        this.params.node?.setDataValue(this.getSharesToMoveColumn(), lotsToMove);
    }

    getTableSharesToMove(): number {
        var columnFieldSplit = this.params.column.getColId().split(' ') //used to get account number appended at the end
        const result = this.params.node.data['Shares to move ' + columnFieldSplit[columnFieldSplit.length - 1]];
        return isNaN(result) ? 0 : Number(result);
    }

    getNodeChangeStatusColumn(){
        var columnFieldSplit = this.params.column.getColId().split(' ')
        return 'nodeChanged ' + columnFieldSplit[columnFieldSplit.length - 1]
    }

    getPricePerShare() {
        return Number(this.params.node.data['Current price/share'].replace('$', '').replaceAll(',',''))
    }

    getvalMultiplier() {
        return Number(this.params.node.data['valMultiplier'])
    }

    /* calculate Estimated market value. shares to move times current price per share times multiplier*/
    calculateEmv(sharesToMove?: number) {
        return (this.getPricePerShare() * this.getvalMultiplier() * Math.sign(this.getAvailableShares()) * (sharesToMove ?? Number(this.enteredSharesToMove)));
    }

    getEmvColumn() {
        var columnFieldSplit = this.params.column.getColId().split(' ')
        return 'Estimated market value ' + columnFieldSplit[columnFieldSplit.length - 1]
    }

    getTotalCostBasisColumn() {
        var columnFieldSplit = this.params.column.getColId().split(' ')
        return 'Total cost basis ' + columnFieldSplit[columnFieldSplit.length - 1]
    }

    getHasErrorColumn() {
        var columnFieldSplit = this.params.column.getColId().split(' ')
        return 'hasError ' + columnFieldSplit[columnFieldSplit.length - 1]
    }

    getMultipleLineErrorCountColumn() {
        var columnFieldSplit = this.params.column.getColId().split(' ');
        return 'multipleLineErrorCount ' + columnFieldSplit[columnFieldSplit.length - 1];
    }

    updateTotalCostBasis(tcb: any) {
        this.params.node?.setDataValue(this.getTotalCostBasisColumn(), this.currencyPipe.transform(tcb))
    }

    getSharesToMoveColumn() {
        var columnFieldSplit = this.params.column.getColId().split(' ')
        return 'Shares to move ' + columnFieldSplit[columnFieldSplit.length - 1]
    }

    getAvailableShares(): number {
        let availableShares = this.params.node.data['Available shares']?.split(' ')[0];
        availableShares = availableShares.replaceAll(',', '');
        return Number(availableShares);
    }

    getCurrentAvailableShares() {
        return Object.keys(this.params.node.data)
            .filter(properties => properties.startsWith('Shares to move'))
            .reduce((availableShares, enteredShare) => {
                const value = parseFloat(this.params.node.data[enteredShare]);
                return parseFloat((availableShares - (isNaN(value) ? 0 : value)).toFixed(8));
            }, Math.abs(this.getAvailableShares()));
    }

    updateStorePositions() {
        const columnFieldSplit = this.params.column.getColId().split(' ');
        const toAccountId = columnFieldSplit[columnFieldSplit.length - 1];
        const byId: RowDataById = {};
        this.store.select(selectMultipleToAccountsPositionLotTableData).pipe(take(1)).subscribe(rowDataById => {
            Object.entries(rowDataById).forEach(([key, rowData]) => {
                if (key === toAccountId) {
                    let positionData = rowData?.positionData?.map(position => {
                        let hasError = this.params.data.schwabSecurityId === position.schwabSecurityId && this.params.data.symbol === position.symbol ? this.params.data[this.getHasErrorColumn()] : position.hasError;
                        if (this.params.data.schwabSecurityId === position.schwabSecurityId && this.params.data.symbol === position.symbol) {
                            return {
                                ...position,
                                'hasError': hasError,
                                'Estimated market value': this.params.data[this.getEmvColumn()]
                            }
                        }
                        else {
                            return {
                                ...position,
                            }
                        }
                    });
                    let data = {
                        positionData: positionData,
                        sharesToMoveTotal: rowData?.sharesToMoveTotal,
                        estimatedMarketValueTotal: rowData?.estimatedMarketValueTotal,
                        nodeCostBasis: this.costBasisMethod ?? ''
                    };
                    byId[key] = data;
                } else {
                    byId[key] = rowData;
                }
            })
        })
        this.store.dispatch(InternalTransfersActions.itUpdateRowData(byId));
    }

    updateEmv(emv: any, EmvColumn?: string) {
        if (this.params.data[this.getHasErrorColumn()] == 0) {
            emv = emv == 0 ? '' : this.currencyPipe.transform(emv)
            this.params.node?.setDataValue(EmvColumn ?? this.getEmvColumn(), emv)
        }
        else {
            this.params.node?.setDataValue(EmvColumn ?? this.getEmvColumn(), '')
        }
    }

    handleTableCalculations() {
        if (!this.showIcon)
        {
            var emv = this.calculateEmv();
            emv == 0 ? this.updateEmv('') : this.updateEmv(emv);
        } else {
            this.updateEmv('');
        }
    }

    // can be reused for adding any tooltip error
    addTooltipError(errorMessage: string): void {
        this.showIcon = true;
        this.toolTipErrorCount++;
        if (this.toolTipErrorCount === 1) {
            // don't show number when only one error in array
            this.setTooltipErrorMessage(errorMessage);
        } else if (this.toolTipErrorArray[0].charAt(0) !== '1') {
            // add number 1 to first error in array
            let error = '1. '.concat(this.toolTipErrorArray[0]);
            this.toolTipErrorArray[0] = error;
            // add second error to array
            this.setTooltipErrorMessage(`${this.toolTipErrorCount}. ${errorMessage}`);
        } else {
            // add 3+ error to array
            this.setTooltipErrorMessage(`${this.toolTipErrorCount}. ${errorMessage}`);
        }
    }

    setTooltipErrorMessage(messageToAdd: string): void {
        this.toolTipErrorArray.push(messageToAdd);
        this.toolTipError = this.toolTipErrorArray.join("\n");
        this.currentValue = this.toolTipErrorCount === 1 ? '1 error' : `${this.toolTipErrorCount} errors`;
    }

    setRowHeight(rowHeight: number): void {
        this.params?.node?.setRowHeight(rowHeight);
        setTimeout(() => {
            this.params?.api?.onRowHeightChanged();
        }, 0);
    }

    get isNotManualFlow() {
        return this.costBasisMethod !== 'None'
    }

    get positionLevelData() {
        return this.params?.data;
    }

    onFocus(event: any) {
        if (this.params.api.getEditingCells().length === 0) {
            this.params.api.startEditingCell({
                rowIndex: this.params.node.rowIndex,
                colKey: this.params.column.getColId()
            });
        }
    }

    validateInput(event: any) {
        const inputElem = event.currentTarget as HTMLInputElement;
        if (event.currentTarget as HTMLInputElement) {
            inputElem.focus();
            var existingInput = this.textInput?.nativeElement?.value ?? "";
            if (!/^\d*\.?\d*$/.test(existingInput + event.key)) {
                return false;
            }
        }
        return true;
    }
}